import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSupabaseClient } from './use-supabase';
import {
  buildAiAgentIdSet,
  buildAiZiwoAgentIdSet,
  buildAiNativeAgentIdSet,
  isAiAgentId,
  isMissingRpc,
  type AiAgentProfile,
} from '../ai-agent-utils';

export interface AiAgentProfilesOptions {
  fetchProfiles?: () => Promise<AiAgentProfile[] | null>;
}

/** Missing RPC, loading and failures leave classification unknown; an empty list is known. */
export function useAiAgentProfiles(orgId: string | null, options?: AiAgentProfilesOptions) {
  const supabase = useSupabaseClient({ optional: true });
  const ready = (supabase !== null || !!options?.fetchProfiles) && !!orgId;
  const query = useQuery({
    queryKey: ['ai-agents', 'profiles', orgId],
    enabled: ready,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      if (options?.fetchProfiles) return options.fetchProfiles();
      if (!supabase) throw new Error('Supabase is not configured');
      const { data, error, status } = await supabase.rpc('ai_agent_profiles');
      if (error) {
        if (isMissingRpc(error.code)) return null;
        // Preserve the CRM rpcCall error fields without importing the host app.
        throw Object.assign(new Error(error.message ?? 'RPC failed'), {
          name: 'RpcError',
          code: error.code,
          operation: 'ai_agent_profiles',
          status,
        });
      }
      return data as AiAgentProfile[] | null;
    },
  });
  return { ...query, ready };
}

/** null means classification is unavailable; report filters must not assert those rows are human. */
export function useIsAiAgent(
  orgId: string | null,
  options?: AiAgentProfilesOptions,
): (userId?: string | null) => boolean | null {
  const { data, isSuccess, ready } = useAiAgentProfiles(orgId, options);
  const ids = useMemo(() => buildAiAgentIdSet(data), [data]);
  return useCallback(
    (userId?: string | null) =>
      ready && isSuccess && Array.isArray(data) ? isAiAgentId(ids, userId) : null,
    [ids, isSuccess, ready, data],
  );
}

/** null also covers an older RPC payload that cannot yet classify provider IDs. */
export function useIsAiZiwoAgent(
  orgId: string | null,
  options?: AiAgentProfilesOptions,
): (agentId?: string | null) => boolean | null {
  const { data, isSuccess, ready } = useAiAgentProfiles(orgId, options);
  const ids = useMemo(() => buildAiZiwoAgentIdSet(data), [data]);
  const available =
    ready && isSuccess && Array.isArray(data) && data.every((p) => 'ziwo_user_id' in p);
  return useCallback(
    (id?: string | null) => (available ? isAiAgentId(ids, id) : null),
    [ids, available],
  );
}

/** Classify atlas_sales.ai_agents.id / ai_chat_agents.id from the same org RPC. */
export function useIsAiNativeAgent(
  orgId: string | null,
  options?: AiAgentProfilesOptions,
): (agentId?: string | null) => boolean | null {
  const { data, isSuccess, ready } = useAiAgentProfiles(orgId, options);
  const ids = useMemo(() => buildAiNativeAgentIdSet(data), [data]);
  return useCallback(
    (id?: string | null) =>
      ready && isSuccess && Array.isArray(data) ? isAiAgentId(ids, id) : null,
    [ids, isSuccess, ready, data],
  );
}
